/* Shared attachment UI for workstream comments + thread notes.
 * `AttachmentList`   - renders existing attachments on a saved comment/note.
 * `AttachmentComposer` - paperclip picker + pending-file pills before posting.
 */
import { useRef } from "react";
import { API, getToken } from "../api";

export const ATTACHMENT_LIMIT_MB = 10;
export const ATTACHMENT_LIMIT_BYTES = ATTACHMENT_LIMIT_MB * 1024 * 1024;
export const ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.md,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx";

const ICON_BY_EXT = {
  pdf: "📄",
  png: "🖼",
  jpg: "🖼",
  jpeg: "🖼",
  gif: "🖼",
  webp: "🖼",
  txt: "📝",
  md: "📝",
  csv: "📊",
  doc: "📄",
  docx: "📄",
  xls: "📊",
  xlsx: "📊",
  ppt: "📑",
  pptx: "📑",
};

function iconFor(filename = "") {
  const ext = (filename.split(".").pop() || "").toLowerCase();
  return ICON_BY_EXT[ext] || "📎";
}

function prettySize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentList({ items, canDelete, onDelete, testIdPrefix }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="att-list" data-testid={`${testIdPrefix}-list`}>
      {items.map((a, idx) => {
        const url = `${API}/files?path=${encodeURIComponent(a.storagePath)}&token=${encodeURIComponent(getToken() || "")}`;
        return (
          <span
            className="att-chip"
            key={a.id || idx}
            data-testid={`${testIdPrefix}-item-${idx}`}
          >
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={a.fileName}
            >
              <span>{iconFor(a.fileName)}</span>
              <span className="att-name">{a.fileName}</span>
              <span className="att-size">{prettySize(a.size)}</span>
            </a>
            {canDelete && (
              <button
                type="button"
                onClick={() => onDelete(idx)}
                title="Remove attachment"
                data-testid={`${testIdPrefix}-item-${idx}-delete`}
              >
                ×
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

export function AttachmentComposer({
  pending,
  setPending,
  testIdPrefix,
  disabled,
}) {
  const ref = useRef(null);

  const handlePick = () => ref.current?.click();
  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of files) {
      if (f.size > ATTACHMENT_LIMIT_BYTES) {
        alert(
          `"${f.name}" is ${prettySize(f.size)} — exceeds the ${ATTACHMENT_LIMIT_MB} MB limit.`
        );
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setPending([...(pending || []), ...accepted]);
    if (ref.current) ref.current.value = "";
  };
  const removePending = (i) => {
    setPending(pending.filter((_, j) => j !== i));
  };

  return (
    <div className="att-composer">
      <button
        type="button"
        className="att-clip"
        onClick={handlePick}
        disabled={disabled}
        data-testid={`${testIdPrefix}-pick`}
        title={`Attach files (PDF, doc, image — ${ATTACHMENT_LIMIT_MB} MB max each)`}
      >
        📎 Attach
      </button>
      <input
        type="file"
        ref={ref}
        accept={ATTACHMENT_ACCEPT}
        multiple
        style={{ display: "none" }}
        onChange={handleChange}
        data-testid={`${testIdPrefix}-input`}
      />
      {pending && pending.length > 0 && (
        <div className="att-pending" data-testid={`${testIdPrefix}-pending`}>
          {pending.map((f, i) => (
            <span className="att-chip att-chip-pending" key={i}>
              <span>{iconFor(f.name)}</span>
              <span className="att-name">{f.name}</span>
              <span className="att-size">{prettySize(f.size)}</span>
              <button
                type="button"
                onClick={() => removePending(i)}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
